import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/ProductsSection.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

function ProductCard({ product }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const SHORT_LIMIT = 50;
  const isLong = product.description.length > SHORT_LIMIT;
  const displayedDesc = expanded
    ? product.description
    : product.description.slice(0, SHORT_LIMIT) + (isLong ? '…' : '');

  const formatPrice = (price) => {
    if (!price) return '';
    let clean = String(price).replace(/dh/i, '').trim();
    let num = parseFloat(clean.replace(',', '.'));
    if (isNaN(num)) {
      return price + ' DH';
    }
    return `${num.toFixed(2).replace('.', ',')} DH`;
  };

  const cleanWhatsAppNumber = (num) => {
    if (!num) return '';
    let cleaned = num.replace(/\D/g, '');
    if (cleaned.startsWith('0') && cleaned.length === 10) {
      cleaned = '212' + cleaned.substring(1);
    }
    return cleaned;
  };

  const whatsappMessage = encodeURIComponent(
    `${t('products.interest')} ${product.name}`
  );
  const whatsappUrl = `https://api.whatsapp.com/send?phone=${cleanWhatsAppNumber(product.whatsapp_number)}&text=${whatsappMessage}`;

  return (
    <div className="product-card">
      <div className="product-card-image-wrapper">
        <img
          src={product.image}
          alt={product.name}
          className="product-card-image"
        />
      </div>
      <div className="product-card-body">
        <h3 className="product-card-name">{product.name}</h3>
        <div className="product-card-desc">
          <p>{displayedDesc}</p>
          {isLong && (
            <button
              className="product-read-more"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? t('products.readLess') : t('products.readMore')}
            </button>
          )}
        </div>
        {product.price && (
          <p className="product-card-price">{formatPrice(product.price)}</p>
        )}
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="product-whatsapp-btn"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="whatsapp-icon">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          {t('products.contactWhatsapp')}
        </a>
      </div>
    </div>
  );
}

function ProductsSection() {
  const { t, i18n } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/products/`, {
      headers: {
        'Accept-Language': i18n.language || 'fr'
      }
    })
      .then((res) => {
        if (!res.ok) throw new Error('Erreur réseau');
        return res.json();
      })
      .then((data) => {
        // DRF router returns paginated or plain list
        setProducts(Array.isArray(data) ? data : data.results || []);
        setLoading(false);
      })
      .catch(() => {
        setError('Impossible de charger les produits.');
        setLoading(false);
      });
  }, [i18n.language]);

  return (
    <section className="products-section">
      <div className="products-container">
        <div className="products-header">
          <h2 className="products-title">
            {t('products.title')} <span className="keyword">{t('products.titleKeyword')}</span>
          </h2>
          <p className="products-subtitle">
            {t('products.subtitle')}
          </p>
        </div>

        {loading && (
          <p className="products-loading">{t('products.loading')}</p>
        )}

        {error && (
          <p className="products-error">{t('products.error')}</p>
        )}

        {!loading && !error && products.length === 0 && (
          <p className="products-loading">{t('products.empty')}</p>
        )}

        {!loading && !error && products.length > 0 && (
          <div className="products-grid">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default ProductsSection;
